import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  Users,
  Eye,
  Inbox,
  TrendingUp,
  Image as ImageIcon,
  Video,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/apiError";
import { SERVICES } from "@/data/services";
import OrphanCleanupCard from "@/pages/admin/OrphanCleanupCard";
import KpiCard from "@/pages/admin/dashboard/KpiCard";
import ChartCard from "@/pages/admin/dashboard/ChartCard";
import ShareNetworksList from "@/pages/admin/dashboard/ShareNetworksList";
import TopReferrersList from "@/pages/admin/dashboard/TopReferrersList";
import {
  RANGES,
  COLORS,
  TOOLTIP_STYLE as tooltipStyle,
} from "@/pages/admin/dashboard/constants";

const DashboardPanel = () => {
  const { authedAxios } = useAuth();
  const { lang } = useLanguage();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const ax = authedAxios();
      const res = await ax.get(`/admin/dashboard?days=${days}`);
      setData(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    queueMicrotask(() => load());
  }, [days]);

  const serviceLabel = (slug) => {
    const s = SERVICES.find((x) => x.slug === slug);
    return s ? slug.toUpperCase() : (slug || "—").toUpperCase();
  };

  const leadsByService = useMemo(
    () =>
      (data?.leads_by_service || []).map((x) => ({
        name: serviceLabel(x.service),
        value: x.count,
      })),
    [data],
  );

  const languages = useMemo(
    () =>
      (data?.languages || []).map((x) => ({
        name: (x.language || "fr").toUpperCase(),
        value: x.count,
      })),
    [data],
  );

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-[#A19E9C] py-20">
        <Loader2 className="w-4 h-4 animate-spin text-[#C5A059]" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-[0.3em]">
          {lang === "fr" ? "Chargement…" : "Loading…"}
        </span>
      </div>
    );
  }

  const t = data?.totals || {};

  return (
    <section data-testid="admin-dashboard-panel" className="space-y-10 pb-20">
      {/* Range selector */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <button
              key={r.v}
              type="button"
              onClick={() => setDays(r.v)}
              data-testid={`dashboard-range-${r.v}`}
              className={`text-[10px] uppercase tracking-[0.25em] px-4 py-2 border transition-colors ${
                days === r.v
                  ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5"
                  : "border-[#F2EBE5]/15 text-[#A19E9C] hover:text-[#F2EBE5] hover:border-[#F2EBE5]/30"
              }`}
            >
              {lang === "fr" ? r.label_fr : r.label_en}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={refreshing}
          data-testid="dashboard-refresh"
          className="kw-btn-ghost"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />
          {lang === "fr" ? "Actualiser" : "Refresh"}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={Eye}
          label={lang === "fr" ? "Vues 24h" : "Views 24h"}
          value={t.views_today ?? 0}
          sub={
            lang === "fr"
              ? `${t.views_range ?? 0} sur la période`
              : `${t.views_range ?? 0} over period`
          }
          testId="kpi-views-today"
        />
        <KpiCard
          icon={Users}
          label={lang === "fr" ? "Visiteurs uniques" : "Unique visitors"}
          value={t.unique_visitors ?? 0}
          sub={
            lang === "fr"
              ? `${t.sessions ?? 0} sessions`
              : `${t.sessions ?? 0} sessions`
          }
          testId="kpi-unique-visitors"
        />
        <KpiCard
          icon={Inbox}
          label={lang === "fr" ? "Demandes" : "Requests"}
          value={t.leads_range ?? 0}
          sub={
            lang === "fr"
              ? `${t.leads_new ?? 0} nouvelles · ${t.leads_total ?? 0} total`
              : `${t.leads_new ?? 0} new · ${t.leads_total ?? 0} total`
          }
          testId="kpi-leads"
        />
        <KpiCard
          icon={TrendingUp}
          label={lang === "fr" ? "Conversion" : "Conversion"}
          value={`${t.conversion_rate ?? 0}%`}
          sub={
            lang === "fr"
              ? "Demandes ÷ sessions"
              : "Requests ÷ sessions"
          }
          testId="kpi-conversion"
        />
      </div>

      {/* Traffic over time */}
      <ChartCard
        title={lang === "fr" ? "Trafic dans le temps" : "Traffic over time"}
        sub={
          lang === "fr"
            ? "Vues, visiteurs uniques et demandes (par jour)"
            : "Views, unique visitors and requests (per day)"
        }
        testId="chart-timeseries"
      >
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data?.timeseries || []}>
            <defs>
              <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#C5A059" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#C5A059" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F2EBE5" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#F2EBE5" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="g3" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#A0834C" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#A0834C" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#F2EBE51A" strokeDasharray="2 4" />
            <XAxis
              dataKey="date"
              tick={{ fill: "#A19E9C", fontSize: 10 }}
              tickFormatter={(d) => d.slice(5)}
            />
            <YAxis tick={{ fill: "#A19E9C", fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#C5A05920" }} />
            <Area type="monotone" dataKey="views" stroke="#C5A059" fill="url(#g1)" strokeWidth={1.5} name={lang === "fr" ? "Vues" : "Views"} />
            <Area type="monotone" dataKey="uniques" stroke="#F2EBE5" fill="url(#g2)" strokeWidth={1} name={lang === "fr" ? "Uniques" : "Uniques"} />
            <Area type="monotone" dataKey="leads" stroke="#A0834C" fill="url(#g3)" strokeWidth={1.5} name={lang === "fr" ? "Demandes" : "Requests"} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Two columns: top pages + leads by service */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard
          title={lang === "fr" ? "Pages les plus consultées" : "Top pages"}
          testId="chart-top-pages"
        >
          {(data?.top_pages || []).length === 0 ? (
            <p className="text-sm text-[#A19E9C] py-8 text-center">
              {lang === "fr" ? "Pas encore de données" : "No data yet"}
            </p>
          ) : (
            <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
              {(data?.top_pages || []).map((p, i) => {
                const max = data.top_pages[0]?.views || 1;
                const pct = Math.round((p.views / max) * 100);
                return (
                  <div key={p.path} data-testid={`top-page-row-${i}`} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <code className="text-xs text-[#F2EBE5] truncate max-w-[60%]">
                        {p.path}
                      </code>
                      <span className="text-xs text-[#A19E9C]">
                        <strong className="text-[#C5A059]">{p.views}</strong> · {p.uniques}u
                      </span>
                    </div>
                    <div className="h-1 bg-[#F2EBE5]/5 overflow-hidden">
                      <div
                        className="h-full bg-[#C5A059] transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ChartCard>

        <ChartCard
          title={lang === "fr" ? "Demandes par service" : "Requests by service"}
          testId="chart-leads-service"
        >
          {leadsByService.length === 0 ? (
            <p className="text-sm text-[#A19E9C] py-8 text-center">
              {lang === "fr" ? "Pas encore de demandes" : "No requests yet"}
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={leadsByService} layout="vertical">
                <CartesianGrid stroke="#F2EBE51A" strokeDasharray="2 4" />
                <XAxis type="number" tick={{ fill: "#A19E9C", fontSize: 10 }} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: "#F2EBE5", fontSize: 11 }}
                  width={100}
                />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#C5A05915" }} />
                <Bar dataKey="value" fill="#C5A059" radius={[0, 0, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Three columns: languages + referrers + portfolio counts */}
      <div className="grid lg:grid-cols-3 gap-6">
        <ChartCard
          title={lang === "fr" ? "Langues" : "Languages"}
          testId="chart-languages"
        >
          {languages.length === 0 ? (
            <p className="text-sm text-[#A19E9C] py-8 text-center">—</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={languages}
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {languages.map((entry, i) => (
                    <Cell key={entry.name || i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ color: "#A19E9C", fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard
          title={lang === "fr" ? "Sources de trafic" : "Top referrers"}
          testId="chart-referrers"
        >
          <TopReferrersList items={data?.top_referrers} lang={lang} />
        </ChartCard>

        <ChartCard
          title={lang === "fr" ? "Partages sociaux" : "Social shares"}
          testId="chart-share-networks"
        >
          <ShareNetworksList items={data?.share_networks} lang={lang} />
        </ChartCard>

        <ChartCard
          title={lang === "fr" ? "Portfolio (médias)" : "Portfolio (media)"}
          testId="chart-portfolio-media"
        >
          {(data?.portfolio_by_service || []).length === 0 ? (
            <p className="text-sm text-[#A19E9C] py-8 text-center">
              {lang === "fr" ? "Aucun média uploadé" : "No media yet"}
            </p>
          ) : (
            <ul className="space-y-3">
              {(data?.portfolio_by_service || []).map((p, i) => (
                <li
                  key={p.service || i}
                  data-testid={`portfolio-stat-${p.service}`}
                  className="border-b border-[#F2EBE5]/5 pb-2 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#F2EBE5]">{serviceLabel(p.service)}</span>
                    <span className="text-xs text-[#A19E9C]">{p.total}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-[10px] text-[#A19E9C]">
                    <span className="inline-flex items-center gap-1">
                      <ImageIcon className="w-3 h-3 text-[#C5A059]" strokeWidth={1.5} />
                      {p.images}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Video className="w-3 h-3 text-[#C5A059]" strokeWidth={1.5} />
                      {p.videos}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>
      </div>

      <OrphanCleanupCard />
    </section>
  );
};

export default DashboardPanel;
